import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, ImagePlaceholder, Plus, ShoppingCart } from "./icons";
import type { ProductGroup } from "../lib/productGroups";
import { useRFQ } from "../context/RFQContext";
import { getProductDisplayText } from "../lib/productDisplay";
import { useLanguage } from "../context/LanguageContext";
import useMediaQuery from "../hooks/useMediaQuery";
import useInView from "../hooks/useInView";

const IMAGE_CYCLE_INTERVAL_MS = 900;
const SWIPE_THRESHOLD_PX = 30;

export default function ProductCard({
  group,
  priority = false,
}: {
  group: ProductGroup;
  /** True for above-the-fold cards (first grid row) — skips lazy-loading
   * and hints the browser to fetch this image first, since it's often the
   * page's LCP element. Defaults to false for every other caller (related
   * products, etc.), which stay lazy as before. */
  priority?: boolean;
}) {
  const { t } = useLanguage();
  const { isSelected, toggleItem } = useRFQ();
  const location = useLocation();
  // Carries the exact filtered catalog view (not just history/sessionStorage,
  // which don't survive a link opened in a new tab) so the product page's
  // "Каталог" breadcrumb can return here with the same filters applied.
  const backParam =
    location.pathname === "/catalog"
      ? `?from=${encodeURIComponent(`${location.pathname}${location.search}`)}`
      : "";
  const product = group.cheapest;
  const isMultiVariant = group.variantCount > 1;
  // For a multi-variant group, "added" means any configuration was added —
  // not just the cheapest one shown on the card — since the actual add
  // happens on the detail page after picking a specific config there.
  const addedVariant = isMultiVariant ? group.products.find((p) => isSelected(p.id)) : undefined;
  const added = isMultiVariant ? addedVariant !== undefined : isSelected(product.id);
  const { cleanedName, cleanedModel, showModel } = getProductDisplayText(
    product.name,
    product.model,
    product.brand
  );

  // Cheapest variant's code only means something when the card represents
  // that one product — a multi-variant group's code differs per
  // configuration, so it's hidden here the same way the model line is.
  const sku =
    !isMultiVariant && typeof product.characteristics?.sku === "string"
      ? product.characteristics.sku
      : undefined;

  // A multi-variant group cycles through one representative photo per
  // variant on hover (so e.g. every color option gets previewed) instead of
  // the cheapest variant's own multi-angle gallery — seeing "here's the
  // other configurations" is more useful on a card than "here's another
  // angle of the same one." Single-variant cards keep the multi-angle
  // gallery cycle, which is the only kind of image variety they have.
  // Deduped: some catalogues (e.g. Tognana) reuse one photo across several
  // size variants of the same shape, and cycling through identical images
  // is just a pointless flicker with nothing to preview.
  // Memoized on group.key/product.id (not recomputed every render) so the
  // preload effect below doesn't refire on every parent re-render.
  const images = useMemo(() => {
    const raw = isMultiVariant
      ? Array.from(new Set(group.products.map((p) => p.image)))
      : product.images?.length
        ? product.images
        : [product.image];
    return raw.filter(Boolean);
  }, [isMultiVariant, group.key, product.id, product.image, product.images]);
  // Only hover-capable pointers (mouse/trackpad) get the hover-driven
  // auto-cycle; touch devices have no hover state, so they auto-cycle
  // instead whenever the card is actually scrolled into view.
  const canHover = useMediaQuery("(hover: hover)");
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const { ref: cardRef, inView } = useInView<HTMLDivElement>(0.5, { once: false });
  const shouldAutoCycle = (canHover ? isHoveringImage : inView) && images.length > 1;

  // Fetch every image up front as soon as a card has more than one to show,
  // instead of only starting the request the instant the cycle reaches it.
  // Without this, the active dot (a cheap class swap) visibly jumps ahead of
  // the <img>, which is still mid-fetch/decode on a cold cache.
  useEffect(() => {
    if (images.length <= 1) return;
    images.forEach((src) => {
      const preload = new Image();
      preload.src = src;
    });
  }, [images]);

  useEffect(() => {
    if (!shouldAutoCycle) return;
    const id = window.setInterval(() => {
      setActiveImageIndex((i) => (i + 1) % images.length);
    }, IMAGE_CYCLE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [shouldAutoCycle, images.length]);

  // Reset back to the first image once cycling stops — on hover-out for
  // desktop, or once the card scrolls back out of view on touch — so the
  // card always shows its primary photo at rest instead of whatever frame
  // the cycle last landed on.
  useEffect(() => {
    if (!shouldAutoCycle) setActiveImageIndex(0);
  }, [shouldAutoCycle]);

  // Touch swipe/drag to cycle images on mobile, where there's no hover
  // state to drive the auto-cycle. Only reacts to touch/pen pointers so it
  // never interferes with the desktop mouse-hover behavior above.
  const touchDragRef = useRef<{ pointerId: number; startX: number } | null>(null);
  const didSwipeRef = useRef(false);

  const handleImagePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" || images.length <= 1) return;
    touchDragRef.current = { pointerId: e.pointerId, startX: e.clientX };
    didSwipeRef.current = false;
  };

  const handleImagePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = touchDragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const delta = e.clientX - drag.startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    didSwipeRef.current = true;
    setActiveImageIndex((i) =>
      delta < 0 ? (i + 1) % images.length : (i - 1 + images.length) % images.length
    );
    touchDragRef.current = { pointerId: drag.pointerId, startX: e.clientX };
  };

  const handleImagePointerUp = () => {
    touchDragRef.current = null;
  };

  // Swiping shouldn't also navigate to the product page — the image sits
  // inside the card's <Link>, so a click fired right after a swipe gets
  // suppressed here, in the capture phase (before it reaches the Link).
  const handleImageClickCapture = (e: React.MouseEvent) => {
    if (didSwipeRef.current) {
      e.preventDefault();
      e.stopPropagation();
      didSwipeRef.current = false;
    }
  };

  return (
    <div
      ref={cardRef}
      className="group relative z-0 flex h-full flex-col border border-steel-200 bg-white transition-shadow duration-300 ease-engineered hover:z-10 hover:shadow-xl"
      onMouseEnter={() => setIsHoveringImage(true)}
      onMouseLeave={() => setIsHoveringImage(false)}
    >
      <Link to={`/catalog/${product.id}${backParam}`} className="contents">
        <div
          className="group/img relative flex aspect-[4/3] touch-pan-y items-center justify-center overflow-hidden bg-white"
          onPointerDown={handleImagePointerDown}
          onPointerMove={handleImagePointerMove}
          onPointerUp={handleImagePointerUp}
          onPointerCancel={handleImagePointerUp}
          onClickCapture={handleImageClickCapture}
        >
          {images.length > 0 ? (
            <img
              src={images[activeImageIndex] ?? images[0]}
              alt={product.name}
              draggable={false}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              className="h-full w-full object-contain transition-transform duration-500 ease-engineered group-hover/img:scale-105"
            />
          ) : (
            <ImagePlaceholder size={40} className="text-steel-300" />
          )}
          {product.stockQuantity !== undefined && product.stockQuantity > 0 && (
            <span className="absolute right-1.5 top-1.5 bg-emerald-700 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide2 text-white sm:right-3 sm:top-3 sm:px-2.5 sm:py-1 sm:text-[10px]">
              {t("inStockBadge")}
            </span>
          )}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center bg-white/70 text-ink opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:bg-white group-hover/img:opacity-100 sm:h-8 sm:w-8"
                aria-label="Previous image"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveImageIndex((prev) => (prev + 1) % images.length);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center bg-white/70 text-ink opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-200 hover:bg-white group-hover/img:opacity-100 sm:h-8 sm:w-8"
                aria-label="Next image"
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 w-1 transition-colors duration-200 ease-engineered ${
                      i === activeImageIndex ? "bg-ink" : "bg-steel-300"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1.5 p-3 sm:gap-3 sm:p-6">
          <div>
            {product.brand && (
              <p className="text-[8px] font-semibold uppercase tracking-wide2 text-accent sm:text-[9px]">
                {product.brand}
              </p>
            )}
            <h3 className="line-clamp-2 font-sans text-base font-semibold leading-tight tracking-[-0.03em] text-ink sm:text-base sm:leading-tight">
              {cleanedName}
            </h3>
            {!isMultiVariant && (showModel ? cleanedModel : sku) && (
              <p className="mt-0.5 line-clamp-2 text-[9px] uppercase leading-snug tracking-wide2 text-steel-500 sm:text-[11px]">
                {showModel ? cleanedModel : sku}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-bold text-ink sm:text-base">
              {group.hasAnyPrice
                ? `${isMultiVariant ? t("pricePrefixFrom") : ""}${group.minPrice!.toLocaleString("ru-RU")} ₸`
                : t("priceOnRequest")}
            </p>
          </div>

        </div>
      </Link>

      {isMultiVariant ? (
        <Link
          to={`/catalog/${addedVariant?.id ?? product.id}${backParam}`}
          className={`flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap px-2 text-center text-[10px] font-semibold uppercase tracking-wide2 transition-colors duration-200 ease-engineered sm:h-12 sm:gap-2 sm:text-xs ${
            added ? "bg-accent text-white hover:bg-accent-hover" : "text-ink hover:text-accent"
          }`}
        >
          {added && <Check size={20} />}
          {added ? t("productAdded") : t("productChooseConfig")}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => toggleItem(product)}
          className={`flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap px-2 text-[10px] font-semibold uppercase tracking-wide2 transition-colors duration-200 ease-engineered sm:h-12 sm:gap-2 sm:text-xs ${
            added
              ? "bg-accent text-white hover:bg-accent-hover"
              : "text-ink hover:text-accent"
          }`}
        >
          {added ? (
            <Check size={22} className="sm:hidden" />
          ) : (
            <span className="flex items-center gap-0.5 sm:hidden">
              <ShoppingCart size={22} strokeWidth={1.2} />
              <Plus size={18} strokeWidth={1.25} />
            </span>
          )}
          {added && <Check size={20} className="hidden sm:block" />}
          <span className="hidden sm:inline">{added ? t("productAdded") : t("productAddToProject")}</span>
        </button>
      )}

      <span className="pointer-events-none absolute inset-0 border border-transparent transition-colors duration-300 ease-engineered group-hover:border-accent" />
    </div>
  );
}
